import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'

import { WEIGHT_DISPLAY_UNIT } from '@/libs/api/types'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { getWeightDisplaySettingOptions } from '../query-options'

/**
 * 選択中拠点の荷物重量の表示形式（単位・小数桁数）を取得するフック（Issue #4905）。
 *
 * 先例の useSiteMiddleName は自社パートナー詳細（GET /partners/in-house）を経由するが、
 * その取得元は Partner インスタンスの CASL チェック（subject('Partner', partner) が
 * users.some.userId===自分 を要求）のため、IN_HOUSE Partner のメンバーではない
 * パートナーユーザーからは 403 になり実質使えない（データ取得は静かに失敗し undefined に
 * フォールバックする）。本設定は自社・パートナー双方に公開する仕様のため、
 * Dc 単位の CASL チェック（subject('Dc', dc)）で認可する専用エンドポイント
 * GET /dcs/:id/weight-display-setting を経由する。
 *
 * 配車板の仮想リストのようにコンポーネントが大量マウントされる箇所での再フェッチを
 * 抑えるため staleTime を設定する（設定値は編集画面からの保存以外で変わらない）。
 */
export const useWeightDisplayFormat = () => {
  const { tenantKey } = useParams({ strict: false })
  const selectedDcId = useAtomValue(selectedDcIdAtom)

  const { data } = useQuery({
    ...getWeightDisplaySettingOptions(tenantKey!, selectedDcId!),
    enabled: !!tenantKey && !!selectedDcId,
    staleTime: 1000 * 60 * 5 // 5分間キャッシュ
  })

  return {
    unit: data?.unit ?? WEIGHT_DISPLAY_UNIT.TON,
    fractionDigits: data?.fractionDigits ?? 4
  }
}
